// Presentation helpers and the palette the badges draw from. These are
// lifted from the original single-file app so the pivoted UI renders
// identically.

import type {
  Availability,
  CalibrationStatus,
  Reservation,
  ResourceType,
} from './types';

export interface BadgeStyle {
  bg: string;
  fg: string;
  bd: string;
}

export const CAL_COLORS: Record<CalibrationStatus, BadgeStyle> = {
  Current: { bg: '#eaf3de', fg: '#3b6d11', bd: '#639922' },
  'Due Soon': { bg: '#faeeda', fg: '#854f0b', bd: '#ef9f27' },
  Overdue: { bg: '#fcebeb', fg: '#a32d2d', bd: '#e24b4a' },
};

export const RESERVATION_COLORS: Record<string, BadgeStyle> = {
  Active: { bg: '#faece7', fg: '#993c1d', bd: '#d85a30' },
  Confirmed: { bg: '#eaf3de', fg: '#3b6d11', bd: '#639922' },
  Pending: { bg: '#faeeda', fg: '#854f0b', bd: '#ef9f27' },
  Canceled: { bg: '#f1efe8', fg: '#5f5e5a', bd: '#b4b2a9' },
};

export const AVAILABILITY_COLORS: Record<Availability, BadgeStyle> = {
  Available: { bg: '#eaf3de', fg: '#3b6d11', bd: '#3b6d11' },
  Reserved: { bg: '#faeeda', fg: '#854f0b', bd: '#854f0b' },
  'In Use': { bg: '#faece7', fg: '#993c1d', bd: '#993c1d' },
};

export const TYPE_COLORS: Record<ResourceType, BadgeStyle> = {
  Equipment: { bg: '#e6f1fb', fg: '#185fa5', bd: '#185fa5' },
  Personnel: { bg: '#eeedfe', fg: '#534ab7', bd: '#534ab7' },
};

/**
 * Today as YYYY-MM-DD in the *browser's* timezone.
 *
 * The original app used `new Date().toISOString().slice(0, 10)`, which
 * is the UTC date — so from early evening onwards in US timezones it
 * reported tomorrow, shifting the "today" column in the availability
 * matrix and the Available/Reserved split by a day. Every date the app
 * handles is a local calendar day, so build the string from local parts.
 */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Local-midnight Date for a YYYY-MM-DD string — never parsed as UTC. */
export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Shift a YYYY-MM-DD string by whole days, staying in local time. */
export function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + days);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * An explicit calibration status from the source of record wins; if
 * there isn't one, derive it from the due date — overdue, within 30
 * days, or current.
 */
export function calibrationStatus(
  nextDue: string,
  explicit: string,
  today: string,
): CalibrationStatus | null {
  if (explicit === 'Current' || explicit === 'Due Soon' || explicit === 'Overdue') {
    return explicit;
  }
  if (!nextDue) return null;
  const days =
    (parseIsoDate(nextDue).getTime() - parseIsoDate(today).getTime()) / 86_400_000;
  if (days < 0) return 'Overdue';
  if (days <= 30) return 'Due Soon';
  return 'Current';
}

/** Up to two initials, for the personnel avatars. */
export function initials(name: string): string {
  return (name || '')
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

/** Bookings that overlap [start, end] for a resource, ignoring cancellations. */
export function overlapping(
  reservations: Reservation[],
  equipmentTitle: string,
  startDate: string,
  endDate: string,
): Reservation[] {
  if (!equipmentTitle || !startDate || !endDate) return [];
  return reservations.filter(
    (r) =>
      r.equipment === equipmentTitle &&
      r.status !== 'Canceled' &&
      r.startDate <= endDate &&
      r.endDate >= startDate,
  );
}
